import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Alert,
  Card,
  Typography,
  Button,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBusinessProfiles } from '../hooks/useBusinessProfiles';
import { useGenerateContent, useContentHistoryByProfile, useWeeklyIdeaPrefill, useGenerationStatus, contentQueryKeys } from '../hooks/useContent';
import { commonQueryKeys, useContentPurposes, useLanguages, useAllPlatforms, useCurrentPlan } from '../hooks/useCommon';
import { useUserAvailablePlatforms } from '../lib/planEntitlements';
import { useAuth } from '../hooks/useAuth';
import { useUpdateProfile } from '../hooks/useAuth';
import PageSection from '../components/PageSection';
import GenerationForm from './content/GenerationForm';
import { toast } from '../lib/toast';
import { translateBackendError } from '../utils/errorTranslator';

const STORAGE_KEYS = {
  lastContent: 'contentGenerator_lastContent',
  lastTimestamp: 'contentGenerator_lastTimestamp',
  ongoingStart: 'contentGenerator_ongoingStart',
  pendingProfile: 'contentGenerator_pendingProfileId',
};

function ContentGenerator() {
  const { t } = useTranslation('content');
  
  const getStoredValue = (key: string): string | null => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(key);
  };

  const scrollToField = (fieldName: string) => {
    if (typeof window === 'undefined') return;
    const element = document.querySelector(`[name="${fieldName}"]`);
    if (element && element.scrollIntoView) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (element instanceof HTMLElement) {
        element.focus();
      }
    }
  };

  const { user } = useAuth();
  const updateProfile = useUpdateProfile();
  const hasSetDefaultLangRef = useRef(false);
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    business_profile_id: '',
    content_purpose: '',
    topic: '',
    keywords: [] as string[],
    platforms: [] as string[],
    additional_context: '',
    language: ''
  });
  
  const [generateImage, setGenerateImage] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>({});
  const [generatedContent, setGeneratedContent] = useState<any>(null);
  const [copiedStates, setCopiedStates] = useState<{ [key: string]: boolean }>({});
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [isRestoringGeneration, setIsRestoringGeneration] = useState(
    () => !!getStoredValue(STORAGE_KEYS.ongoingStart)
  );
  const [pendingProfileId, setPendingProfileId] = useState<string | null>(() =>
    getStoredValue(STORAGE_KEYS.pendingProfile)
  );
  const [pendingGenerationId, setPendingGenerationId] = useState<number | null>(null);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const prefillAppliedRef = useRef(false);

  const queryClient = useQueryClient();
  const generateContent = useGenerateContent();
  const generationStatus = useGenerationStatus(pendingGenerationId);

  // Restore last generated content and check for ongoing generation on mount
  useEffect(() => {
    // First, try to restore from localStorage
    const savedContent = localStorage.getItem(STORAGE_KEYS.lastContent);
    if (savedContent) {
      try {
        const parsed = JSON.parse(savedContent);
        const savedTimestamp = localStorage.getItem(STORAGE_KEYS.lastTimestamp);
        if (savedTimestamp) {
          const timeDiff = Date.now() - parseInt(savedTimestamp, 10);
          const oneHour = 60 * 60 * 1000;
          // Only restore if recent (within 1 hour)
          if (timeDiff < oneHour) {
            setGeneratedContent(parsed);
          }
        }
      } catch (e) {
        // console.error('Failed to restore saved content:', e);
      }
    }

    // Check if there's an ongoing generation
    const ongoingStart = localStorage.getItem(STORAGE_KEYS.ongoingStart);
    if (ongoingStart) {
      const startTime = parseInt(ongoingStart, 10);
      const timeSinceStart = Date.now() - startTime;
      const maxWaitTime = 10 * 60 * 1000; // 10 minutes
      
      if (timeSinceStart < maxWaitTime) {
        // Generation might still be ongoing, show loading
        setIsRestoringGeneration(true);
        setGeneratedContent(null);
      } else {
        // Stale marker, remove it
        localStorage.removeItem(STORAGE_KEYS.ongoingStart);
        localStorage.removeItem(STORAGE_KEYS.pendingProfile);
        setPendingProfileId(null);
      }
    }
  }, []); // Only run on mount

  // Restore in-progress async generation from localStorage on mount
  useEffect(() => {
    const savedId = localStorage.getItem('pending_generation_id');
    const savedStart = localStorage.getItem('ongoing_generation_start');
    if (savedId && savedStart) {
      const elapsed = Date.now() - parseInt(savedStart, 10);
      if (elapsed < 10 * 60 * 1000) {
        setPendingGenerationId(parseInt(savedId, 10));
      } else {
        localStorage.removeItem('pending_generation_id');
        localStorage.removeItem('ongoing_generation_start');
      }
    }
  }, []);

  // Timeout polling after 10 minutes if we never get a success/failed response
  useEffect(() => {
    if (!pendingGenerationId) return;
    const timer = setTimeout(() => {
      setPendingGenerationId(null);
      setPollingTimedOut(true);
      localStorage.removeItem('pending_generation_id');
      localStorage.removeItem('ongoing_generation_start');
    }, 10 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [pendingGenerationId]);

  // Queries
  const ideaId = searchParams.get('idea');
  const { data: weeklyIdeaPrefill } = useWeeklyIdeaPrefill(ideaId);
  const { data: businessProfiles, isLoading: loadingProfiles } = useBusinessProfiles();
  const { data: contentPurposes } = useContentPurposes();
  const { data: availablePlatforms } = useUserAvailablePlatforms();
  const { data: allPlatforms } = useAllPlatforms();
  const { data: languages } = useLanguages();
  const { data: currentPlan } = useCurrentPlan(user?.id);
  const imageGenerationEnabled = Boolean(currentPlan?.image_generation_enabled);

  // Set default language when languages are loaded
  useEffect(() => {
    if (languages && languages.length > 0 && !formData.language) {
      // Priority: user's content_generation_language > user's preferred_language > English > first available
      let defaultLang: any = null;
      let shouldSaveToDB = false;
      
      // 1. Check user's content generation language preference
      if (user?.content_generation_language) {
        defaultLang = languages.find((l: any) => l.code === user.content_generation_language);
      }
      
      // 2. Fallback to user's UI language preference
      if (!defaultLang && user?.preferred_language) {
        defaultLang = languages.find((l: any) => l.code === user.preferred_language);
        // If we're using preferred_language and user doesn't have content_generation_language set, save it
        if (defaultLang && !user?.content_generation_language && !hasSetDefaultLangRef.current) {
          shouldSaveToDB = true;
          hasSetDefaultLangRef.current = true;
        }
      }
      
      // 3. Fallback to English
      if (!defaultLang) {
        defaultLang = languages.find((l: any) => l.code === 'en');
        // If we're using English fallback and user doesn't have content_generation_language set, save it
        if (defaultLang && !user?.content_generation_language && !hasSetDefaultLangRef.current) {
          shouldSaveToDB = true;
          hasSetDefaultLangRef.current = true;
        }
      }
      
      // 4. Ultimate fallback to first available language
      if (!defaultLang && languages.length > 0) {
        defaultLang = languages[0];
        // If we're using first available and user doesn't have content_generation_language set, save it
        if (defaultLang && !user?.content_generation_language && !hasSetDefaultLangRef.current) {
          shouldSaveToDB = true;
          hasSetDefaultLangRef.current = true;
        }
      }
      
      if (defaultLang && defaultLang.code) {
        setFormData((prev: any) => ({ ...prev, language: defaultLang.code }));
        
        // Save to DB if user doesn't have content_generation_language set
        if (shouldSaveToDB && user) {
          updateProfile.mutateAsync({
            content_generation_language: defaultLang.code
          }).catch(err => {
            // console.error('Failed to set default content generation language:', err);
            hasSetDefaultLangRef.current = false; // Reset on error so we can try again
          });
        }
      }
    }
  }, [languages, user?.content_generation_language, user?.preferred_language, formData.language, user, updateProfile]);
  
  // Get the active profile ID for history checking
  const activeProfileId =
    formData.business_profile_id ||
    pendingProfileId ||
    (businessProfiles?.[0]?.id?.toString() || '');

  // Poll history API if there's an ongoing generation
  const { data: historyData } = useContentHistoryByProfile(
    activeProfileId,
    {
      enabled: !!activeProfileId,
      refetchInterval: isRestoringGeneration && !generatedContent ? 3000 : false, // Poll every 3 seconds while restoring
    }
  );

  // Check history for the latest generation when polling
  useEffect(() => {
    if (
      isRestoringGeneration &&
      !generatedContent &&
      historyData?.data &&
      historyData.data.length > 0
    ) {
      const ongoingStart = localStorage.getItem(STORAGE_KEYS.ongoingStart);
      if (ongoingStart) {
        const startTime = parseInt(ongoingStart, 10);
        
        // Get the most recent generation
        const latestGeneration = historyData.data[0];
        
        // Check if this generation was created after we started waiting
        if (latestGeneration.created_at) {
          const generationTime = new Date(latestGeneration.created_at).getTime();
          
          // If the latest generation is newer than when we started, use it
          if (generationTime >= startTime - 5000) { // 5 second buffer for timing differences
            setGeneratedContent(latestGeneration);
            setIsRestoringGeneration(false);
            localStorage.removeItem(STORAGE_KEYS.ongoingStart);
            localStorage.removeItem(STORAGE_KEYS.pendingProfile);
            setPendingProfileId(null);
            // Save to localStorage
            localStorage.setItem(STORAGE_KEYS.lastContent, JSON.stringify(latestGeneration));
            localStorage.setItem(STORAGE_KEYS.lastTimestamp, Date.now().toString());
            toast.success('content.generatedSuccess');
          }
        }
      }
    }
  }, [historyData, isRestoringGeneration, generatedContent, pendingProfileId, t]);

  // When not restoring, always show the latest generation from history
  useEffect(() => {
    if (isRestoringGeneration) {
      return;
    }
    const latestGeneration = historyData?.data?.[0];
    if (!latestGeneration) {
      return;
    }
    if (!generatedContent || generatedContent.id !== latestGeneration.id) {
      setGeneratedContent(latestGeneration);
    }
  }, [historyData, isRestoringGeneration, generatedContent]);

  // Stop polling after max wait time
  useEffect(() => {
    if (isRestoringGeneration) {
      const ongoingStart = localStorage.getItem(STORAGE_KEYS.ongoingStart);
      if (ongoingStart) {
        const startTime = parseInt(ongoingStart, 10);
        const maxWaitTime = 10 * 60 * 1000; // 10 minutes
        
        const timeout = setTimeout(() => {
          const timeSinceStart = Date.now() - startTime;
          if (timeSinceStart >= maxWaitTime) {
            setIsRestoringGeneration(false);
            localStorage.removeItem(STORAGE_KEYS.ongoingStart);
            localStorage.removeItem(STORAGE_KEYS.pendingProfile);
            setPendingProfileId(null);
          }
        }, maxWaitTime - (Date.now() - startTime));

        return () => clearTimeout(timeout);
      }
    }
  }, [isRestoringGeneration]);

  // Handle polling result from useGenerationStatus
  useEffect(() => {
    const statusData = generationStatus.data;
    if (!statusData) return;

    if (statusData.status === 'success') {
      // Pass the full status object so extractGeneratedContent can find the
      // generated_content field inside it — same pattern as the history polling
      setGeneratedContent(statusData);
      setShowScrollToTop(true);
      // Clear in-flight tracking
      setPendingGenerationId(null);
      localStorage.removeItem('pending_generation_id');
      localStorage.removeItem('ongoing_generation_start');
      // Clear old ongoing marker keys too
      localStorage.removeItem(STORAGE_KEYS.ongoingStart);
      localStorage.removeItem(STORAGE_KEYS.pendingProfile);
      setPendingProfileId(null);
      setIsRestoringGeneration(false);
      // Invalidate queries so history and usage reflect the new generation
      queryClient.invalidateQueries({ queryKey: contentQueryKeys.generations() });
      queryClient.invalidateQueries({ queryKey: contentQueryKeys.history() });
      queryClient.invalidateQueries({ queryKey: commonQueryKeys.usage() });
      toast.success('content.generatedSuccess');
    } else if (statusData.status === 'failed') {
      // Clear in-flight tracking
      setPendingGenerationId(null);
      localStorage.removeItem('pending_generation_id');
      localStorage.removeItem('ongoing_generation_start');
      localStorage.removeItem(STORAGE_KEYS.ongoingStart);
      localStorage.removeItem(STORAGE_KEYS.pendingProfile);
      setPendingProfileId(null);
      setIsRestoringGeneration(false);
      setError(statusData.error_message || t('generation.errors.failed'));
    }
  }, [generationStatus.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save generated content to localStorage whenever it changes
  useEffect(() => {
    if (generatedContent && generatedContent.id) {
      localStorage.setItem(STORAGE_KEYS.lastContent, JSON.stringify(generatedContent));
      localStorage.setItem(STORAGE_KEYS.lastTimestamp, Date.now().toString());
      // Clear ongoing marker since we have the result
      localStorage.removeItem(STORAGE_KEYS.ongoingStart);
      localStorage.removeItem(STORAGE_KEYS.pendingProfile);
      setPendingProfileId(null);
      setIsRestoringGeneration(false);
    }
  }, [generatedContent]);

  // Scroll to top when content is generated
  useEffect(() => {
    if (generatedContent) {
      setShowScrollToTop(true);
      // Scroll to top after a short delay to ensure content is rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [generatedContent]);
  
  // Use user data directly instead of separate API call
  const emailStatus = { is_verified: user?.is_verified || true };

  // Check if user has social media access
  const hasSocialMediaAccess = Boolean(availablePlatforms && availablePlatforms.length > 0);
  
  // Check if user is blocked by email verification
  const isBlocked = user?.role === 'client' && emailStatus && !emailStatus.is_verified;

  // Handle profile query parameter from URL (takes precedence)
  useEffect(() => {
    const profileParam = searchParams.get('profile');
    if (profileParam && businessProfiles && businessProfiles.length > 0) {
      // Check if the profile ID from URL exists in user's business profiles
      const profileExists = businessProfiles.some(
        (profile: any) => profile.id.toString() === profileParam
      );
      if (profileExists) {
        setFormData(prev => ({
          ...prev,
          business_profile_id: profileParam
        }));
      }
    }
  }, [searchParams, businessProfiles]);

  // Reset prefill-applied flag when idea param is removed (e.g. user cleared URL)
  useEffect(() => {
    if (!ideaId) {
      prefillAppliedRef.current = false;
    }
  }, [ideaId]);

  // Pre-fill form from weekly content idea (email CTA: ?profile=...&idea=...)
  useEffect(() => {
    if (!weeklyIdeaPrefill || prefillAppliedRef.current || !businessProfiles?.length) {
      return;
    }
    const profileIdStr = String(weeklyIdeaPrefill.business_profile_id);
    const profileExists = businessProfiles.some(
      (p: any) => p.id.toString() === profileIdStr
    );
    if (!profileExists) {
      return;
    }
    prefillAppliedRef.current = true;
    setFormData(prev => ({
      ...prev,
      business_profile_id: profileIdStr,
      content_purpose: weeklyIdeaPrefill.content_purpose || prev.content_purpose,
      topic: weeklyIdeaPrefill.topic || prev.topic,
      keywords: Array.isArray(weeklyIdeaPrefill.keywords) ? weeklyIdeaPrefill.keywords : prev.keywords,
      additional_context: weeklyIdeaPrefill.additional_context || prev.additional_context,
      language: weeklyIdeaPrefill.language || prev.language,
    }));
  }, [weeklyIdeaPrefill, businessProfiles]);

  // Auto-select business profile for regular users (only if no profile param in URL)
  useEffect(() => {
    const profileParam = searchParams.get('profile');
    if (!profileParam && user?.role === 'client' && businessProfiles && businessProfiles.length > 0) {
      // Only auto-select if no profile is already selected
      if (!formData.business_profile_id) {
        setFormData(prev => ({
          ...prev,
          business_profile_id: businessProfiles[0].id.toString()
        }));
      }
    }
  }, [user?.role, businessProfiles, searchParams, formData.business_profile_id]);

  // Ensure pending generation profile stays selected while waiting
  useEffect(() => {
    if (!pendingProfileId || !businessProfiles || businessProfiles.length === 0) {
      return;
    }
    const exists = businessProfiles.some(
      (profile: any) => profile.id.toString() === pendingProfileId
    );
    if (!exists) {
      return;
    }
    if (formData.business_profile_id !== pendingProfileId) {
      setFormData(prev => ({
        ...prev,
        business_profile_id: pendingProfileId,
      }));
    }
  }, [pendingProfileId, businessProfiles, formData.business_profile_id]);

  // Track the last business profile ID to detect changes
  const [lastBusinessProfileId, setLastBusinessProfileId] = useState<string>('');

  // Auto-select preferred platforms when business profile is selected or changed
  useEffect(() => {
    // Only proceed if we have the necessary data
    if (!formData.business_profile_id || !businessProfiles || !allPlatforms || !availablePlatforms) {
      return;
    }

    // Check if business profile changed or if platforms are empty (initial load)
    const businessProfileChanged = formData.business_profile_id !== lastBusinessProfileId;
    const platformsEmpty = !formData.platforms || formData.platforms.length === 0;

    // Only auto-select if:
    // 1. Business profile changed (user selected a different profile)
    // 2. OR platforms are empty (initial page load)
    if (!businessProfileChanged && !platformsEmpty) {
      return; // Don't override user's manual selection
    }

    // Find the selected business profile
    const selectedProfile = businessProfiles.find(
      (profile: any) => profile.id.toString() === formData.business_profile_id
    );

    // If profile has preferred platforms, map them to platform names
    if (selectedProfile?.preferred_platform_ids && selectedProfile.preferred_platform_ids.length > 0) {
      // Create a set of available platform IDs for quick lookup
      const availablePlatformIds = new Set((availablePlatforms || []).map((p: any) => p.id));
      
      // Map preferred platform IDs to platform names
      // Only include platforms that are:
      // 1. In the preferred_platform_ids array
      // 2. Available in the user's plan
      const preferredPlatformNames = selectedProfile.preferred_platform_ids
        .map((platformId: number) => {
          // Find the platform in allPlatforms
          const platform = allPlatforms.find((p: any) => p.id === platformId);
          // Only return if platform exists and is available in user's plan
          if (platform && availablePlatformIds.has(platformId)) {
            return platform.name;
          }
          return null;
        })
        .filter((name: string | null): name is string => name !== null);

      // Update platforms with preferred ones
      if (preferredPlatformNames.length > 0) {
        setFormData(prev => ({
          ...prev,
          platforms: preferredPlatformNames,
        }));
      } else if (businessProfileChanged) {
        // If business profile changed but no preferred platforms are available, clear selection
        setFormData(prev => ({
          ...prev,
          platforms: [],
        }));
      }
    } else if (businessProfileChanged) {
      // If business profile changed but has no preferred platforms, clear selection
      setFormData(prev => ({
        ...prev,
        platforms: [],
      }));
    }

    // Update the last business profile ID
    setLastBusinessProfileId(formData.business_profile_id);
  }, [formData.business_profile_id, formData.platforms, businessProfiles, allPlatforms, availablePlatforms, lastBusinessProfileId]);

  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear field error when user makes a selection
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const handleKeywordAdd = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const handleKeywordRemove = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  const validateForm = () => {
    setError('');
    setFieldErrors({});
    
    const hasBusinessProfile = formData.business_profile_id !== '';
    const hasContentPurpose = formData.content_purpose !== '';
    const hasMultipleProfiles = businessProfiles && businessProfiles.length > 1;
    const hasPlatforms = formData.platforms && formData.platforms.length > 0;
    
    // Check if user has multiple business profiles but hasn't selected one
    if (!hasBusinessProfile && hasMultipleProfiles) {
      setError(t('generation.errors.noProfile'));
      setFieldErrors({ business_profile: true });
      scrollToField('business_profile_id');
      return false;
    }
    
    // Check if content purpose is missing
    if (!hasContentPurpose) {
      setError(t('generation.errors.noPurpose'));
      setFieldErrors({ content_purpose: true });
      scrollToField('content_purpose');
      return false;
    }
    
    // Check if at least one platform is selected (only for users with social media access)
    if (hasSocialMediaAccess && !hasPlatforms) {
      setError(t('generation.errors.noPlatform'));
      setFieldErrors({ platforms: true });
      scrollToField('platforms');
      return false;
    }
    
    return true;
  };

  const handleGenerateSingle = async () => {
    if (!validateForm()) return;
    
    // Clear any previously displayed content while new generation runs
    setGeneratedContent(null);
    setShowScrollToTop(false);
    localStorage.removeItem(STORAGE_KEYS.lastContent);
    localStorage.removeItem(STORAGE_KEYS.lastTimestamp);

    // Mark generation as ongoing
    const profileIdForGeneration =
      formData.business_profile_id ||
      pendingProfileId ||
      (businessProfiles?.[0]?.id?.toString() || '');
    if (profileIdForGeneration) {
      localStorage.setItem(STORAGE_KEYS.pendingProfile, profileIdForGeneration);
      setPendingProfileId(profileIdForGeneration);
    }
    localStorage.setItem(STORAGE_KEYS.ongoingStart, Date.now().toString());
    setIsRestoringGeneration(true);
    setPollingTimedOut(false);

    try {
      const pending = await generateContent.mutateAsync({
        business_profile_id: Number(formData.business_profile_id),
        content_purpose: formData.content_purpose, // Fixed: changed from content_type to content_purpose
        topic: formData.topic || undefined, // Fixed: changed from prompt to topic
        keywords: formData.keywords.length > 0 ? formData.keywords : undefined,
        platforms: formData.platforms.length > 0 ? formData.platforms : undefined,
        additional_context: formData.additional_context || undefined,
        language: formData.language || undefined,
        generate_image: generateImage || undefined,
      });

      // Backend returned 202 - store the generation_id and start polling
      setPendingGenerationId(pending.generation_id);
      localStorage.setItem('pending_generation_id', String(pending.generation_id));
      localStorage.setItem('ongoing_generation_start', Date.now().toString());
      // Results will arrive via the generationStatus polling useEffect above
    } catch (error: any) {
      // Clear ongoing marker on error
      localStorage.removeItem(STORAGE_KEYS.ongoingStart);
      localStorage.removeItem(STORAGE_KEYS.pendingProfile);
      localStorage.removeItem('pending_generation_id');
      localStorage.removeItem('ongoing_generation_start');
      setPendingProfileId(null);
      setIsRestoringGeneration(false);
      // Extract error message safely
      let errorMessage = t('generation.errors.failed');
      
      if (error?.response?.data) {
        const data = error.response.data;
        
        // Handle FastAPI validation errors (array of error objects)
        if (Array.isArray(data)) {
          errorMessage = data.map((err: any) => {
            let msg = '';
            if (typeof err === 'string') msg = err;
            else if (err?.msg) msg = err.msg;
            else if (err?.message) msg = err.message;
            else msg = JSON.stringify(err);
            // Translate backend error messages
            return translateBackendError(msg);
          }).join(', ');
        } else if (typeof data === 'object' && data.detail) {
          if (typeof data.detail === 'string') {
            errorMessage = translateBackendError(data.detail);
          } else if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map((err: any) => {
              let msg = '';
              if (typeof err === 'string') msg = err;
              else if (err?.msg) msg = err.msg;
              else if (err?.message) msg = err.message;
              else msg = JSON.stringify(err);
              // Translate backend error messages
              return translateBackendError(msg);
            }).join(', ');
          }
        } else if (data.message && typeof data.message === 'string') {
          errorMessage = translateBackendError(data.message);
        }
      } else if (error?.message && typeof error.message === 'string') {
        errorMessage = translateBackendError(error.message);
      }
      
      setError(errorMessage);
    }
  };

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      // console.error('Failed to copy text: ', err);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const displayedContent = isRestoringGeneration ? null : generatedContent;

  if (loadingProfiles) {
    return (
      <PageSection
        title={t('generation.title')}
        loading={true}
        loadingText={t('generation.loading')}
        hideTitleOnMobile
      >
        <div />
      </PageSection>
    );
  }

  // Show error if no business profiles for regular users
  if (user?.role === 'client' && (!businessProfiles || businessProfiles.length === 0)) {
    return (
      <PageSection title={t('generation.title')} hideTitleOnMobile>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Card sx={{ maxWidth: 500, p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="error">
              {t('generation.noProfileFound')}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {t('generation.noProfileFoundDesc')}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => window.location.href = '/business-profiles'}
              sx={{ mt: 2 }}
            >
              {t('generation.createBusinessProfile')}
            </Button>
          </Card>
        </Box>
      </PageSection>
    );
  }

  // Email verification blocking overlay
  if (isBlocked) {
    return (
      <PageSection title={t('generation.title')} hideTitleOnMobile>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Card sx={{ maxWidth: 500, p: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom color="error">
              {t('generation.emailVerificationRequired')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('generation.emailVerificationDesc')}
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                onClick={() => window.location.href = '/verify-email'}
              >
                {t('generation.verifyEmail')}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => window.location.href = '/profile'}
              >
                {t('generation.goToProfile')}
              </Button>
            </Box>
          </Card>
        </Box>
      </PageSection>
    );
  }

  return (
    <PageSection
      title={t('generation.title')}
      subtitle={hasSocialMediaAccess 
        ? t('generation.subtitle')
        : t('generation.subtitleEmail')}
      hideTitleOnMobile
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <GenerationForm
          formData={formData}
          setFormData={setFormData}
          keywordInput={keywordInput}
          setKeywordInput={setKeywordInput}
          businessProfiles={businessProfiles || []}
          contentPurposes={contentPurposes || []}
          languages={languages || []}
          user={user}
          hasSocialMediaAccess={hasSocialMediaAccess}
          fieldErrors={fieldErrors}
          onSelectChange={handleSelectChange}
          onKeywordAdd={handleKeywordAdd}
          onKeywordRemove={handleKeywordRemove}
          onGenerate={handleGenerateSingle}
          isGenerating={generateContent.isPending || isRestoringGeneration || pendingGenerationId !== null}
          generatedContent={displayedContent}
          copiedStates={copiedStates}
          showScrollToTop={showScrollToTop}
          onCopy={handleCopy}
          onRegenerate={handleGenerateSingle}
          onScrollToTop={scrollToTop}
          generateImage={generateImage}
          onGenerateImageChange={setGenerateImage}
          imageGenerationEnabled={imageGenerationEnabled}
        />
      </Box>
    </PageSection>
  );
}

export default ContentGenerator;
